import { useEffect } from 'react';
import { useRouter } from 'next/router';
import type { bmPageLoadSrc172Evid501Params } from '@wix/bi-logger-scheduling-assistant-data/v2/types';
import { useGenericBi } from './use-generic-bi';
import { biPageName } from './bi-page-name';
import { currentArrival } from './arrival-attribution';

export type UsePageLoadBiOptions = {
  enabled?: boolean;
  /** Register browser-back BI. Only one per route — enable at page level, not nested views. */
  trackBack?: boolean;
  /**
   * Extra load-state context for the generic page-view (loadFinish, evid 5) event only —
   * e.g. which connectors came back already-connected. Not sent on the src:172 event.
   * Capture it once the page's data is ready (gate with `enabled`).
   */
  additionalFields?: Record<string, string>;
  /**
   * Real load outcome for the generic loadFinish (evid 5). When the page gates
   * `enabled` on its data being ready, pass `false` if that data resolved to an
   * error so the beacon reflects the true outcome. Defaults to `true`.
   */
  isSuccessful?: boolean;
  /**
   * The page's name in the shared three-surface vocabulary, when the ROUTE cannot answer it.
   * Everything else leaves it to `biPageName(router.pathname)`.
   */
  pageName?: string;
  /**
   * What sent the visitor to this page, reported as the generic loadFinish's `referral_info`
   * (src:198 only). Opt-in per page: a page passes it only when it validates where its value came
   * from, as the picker does with `readUpgradeAttribution`.
   */
  referralInfo?: string;
};

// The page's own route provider can't tag events fired at the page component's
// top level (the hook runs before that provider mounts). So pass the canonical page
// name as componentParentName — that keeps page-load (and back-click) events from
// having a null componentRoute, and makes the route's first segment the same page
// name the event's own `pageName` carries, matching the native apps' two-segment
// `page / component` route.
export const usePageLoadBi = (
  params: bmPageLoadSrc172Evid501Params,
  options: boolean | UsePageLoadBiOptions = true,
) => {
  const router = useRouter();
  const pageNameOverride = typeof options === 'boolean' ? undefined : options.pageName;
  const pageName = pageNameOverride ?? biPageName(router.pathname, router.query);
  const { logPageClickBi, logPageLoadBi } = useGenericBi(pageName);
  const enabled = typeof options === 'boolean' ? options : (options.enabled ?? true);
  const trackBack = typeof options === 'boolean' ? false : (options.trackBack ?? false);
  const additionalFields = typeof options === 'boolean' ? undefined : options.additionalFields;
  const isSuccessful = typeof options === 'boolean' ? undefined : options.isSuccessful;
  const referralInfo = typeof options === 'boolean' ? undefined : options.referralInfo;

  useEffect(() => {
    if (!enabled) return;
    // Page views on the route a visit landed on carry where it came from (utmSource/utmMedium/
    // utmCampaign/utmContent), so an arrival from a Wix email or the Business Manager is its own
    // row even when no pack is accepted. The page's own fields win on a clash.
    const arrival = currentArrival();
    const fields = additionalFields || Object.keys(arrival).length > 0 ? { ...arrival, ...additionalFields } : undefined;
    logPageLoadBi(params, {
      // The generic page-view's componentName is the page itself, named in the shared
      // vocabulary — not the src:172 page_name, which is a different (longer-standing)
      // naming scheme. Native does the same: `componentName == kebab(biName)`.
      componentName: pageName,
      ...(fields ? { additionalFields: fields } : {}),
      ...(isSuccessful !== undefined ? { isSuccessful } : {}),
      ...(referralInfo ? { referralInfo } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !trackBack) return;
    router.beforePopState(() => {
      logPageClickBi({
        page_name: params.page_name,
        page_version: params.page_version,
        origin: params.origin,
        button_name: 'back',
      });
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, trackBack, router, params.page_name, params.page_version, params.origin]);
};
